
import { Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import Notification from '@/mixins/Notification';
import StackedForm from '@/mixins/StackedForm';
import { Venue } from '@/interfaces/models/Venue';
import { namespace } from 'vuex-class';
import { Upsell, AddonUpsell as AddonUpsellModal, GoalUpsell as GoalUpsellModel } from '@/interfaces/models/Upsell';
import { UpsellType } from '@/enums/UpsellType';
import { isAxiosError } from '@/util/helper';
import VTable from '@/components/shared/table/VTable.vue';
import { ADDON_HEADERS } from '@/components/upsell/headers';
import AddonForm from '@/components/upsell/AddonForm.vue';
import FoodcardApiService from '@/api/http/FoodcardApiService';
import { AxiosResponse } from 'axios';
import { Article } from '@/interfaces/models/Article';

const app = namespace('app');
const venue = namespace('venue');
const upsell = namespace('upsell');

@Component({
  components: { AddonForm, VTable },
})
export default class AddonUpsell extends mixins(Notification, StackedForm) {
  public headers = ADDON_HEADERS;

  @venue.State('active') public venue!: Venue;
  @app.State('venues') public venues!: Venue[];

  @upsell.State('addon') public upsell!: Upsell<AddonUpsellModal>;
  @upsell.Action('showByVenueAndType') public showByVenueAndType!: any;
  @upsell.Action('store') public store!: any;
  @upsell.Action('update') public update!: any;
  @upsell.Action('destroy') public destroy!: any;

  public editModalVisible: boolean = false;
  public articles: Partial<Article[]> = [];
  public activeUpsellItem: AddonUpsellModal | null = null;
  public isMaster: boolean = false;

  public $refs!: {
    form: InstanceType<typeof AddonForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
    };
  };

  public async mounted() {
    if (!this.venue) return;
    this.reload();
  }

  protected async reload() {
    await this.loadArticles();
    try {
      await this.showByVenueAndType({ type: UpsellType.ADDON, venue: this.venue._id });
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        return;
      }
      throw err;
    } finally {
      this.isMaster = this.upsell?.isMaster || false;
    }
  }

  protected async loadArticles() {
    if (!this.venue) {
      return;
    }
    const api: FoodcardApiService = new FoodcardApiService();
    await api.getArticleNames({ venue: this.venue._id }).then((res: AxiosResponse<Partial<Article>[]>) => {
      this.articles = res.data as any;
    });
  }

  protected getNextPosition() {
    if (!this.upsell || this.upsell.upsells.length === 0) {
      return 1;
    }

    const nextInOrder = this.upsell.upsells.length + 1;
    const maxPosition = this.upsell.upsells.reduce((max, u) => Math.max(max, u.position || 0), 0);

    return nextInOrder !== maxPosition ? nextInOrder : maxPosition + 1;
  }

  protected reorder(upsells: AddonUpsellModal[]) {
    return upsells.map((upsell: AddonUpsellModal, index: number) => {
      return { ...upsell, position: index + 1 };
    });
  }

  protected async save() {
    this.$refs.form.validate().then(async (result: boolean | boolean[]) => {
      if (!this.isValid(result)) {
        this.notifyError('notification.form');
        return;
      }

      const data: AddonUpsellModal = this.$refs.form.getData();

      this.$startLoading('upsell.save');

      if (this.activeUpsellItem) {
        const index = this.upsell.upsells.findIndex((upsell: AddonUpsellModal) => upsell === this.activeUpsellItem);
        this.upsell.upsells[index] = { ...this.activeUpsellItem, ...data };

        await this.update({
          id: this.upsell._id,
          ...this.upsell,
          isMaster: this.isMaster,
          upsells: [...this.upsell.upsells],
        });
      } else if (this.upsell) {
        await this.update({
          id: this.upsell._id,
          ...this.upsell,
          isMaster: this.isMaster,
          upsells: [...this.upsell.upsells, { ...data, position: this.getNextPosition() }],
        });
      } else {
        await this.store({
          type: UpsellType.ADDON,
          venue: this.venue._id,
          isMaster: this.isMaster,
          upsells: [{ ...data, position: this.getNextPosition() }],
        });
      }
      this.$stopLoading('upsell.save');
      this.reset();
    });
  }

  protected reset() {
    this.editModalVisible = false;
    this.activeUpsellItem = null;
  }

  protected async deleteUpsell(item: AddonUpsellModal) {
    this.$startLoading('upsell.save');

    if (this.upsell.upsells.length === 1) {
      await this.destroy({ id: this.upsell._id, type: UpsellType.ADDON });
      this.$stopLoading('upsell.save');
      this.isMaster = false;
      return;
    }

    const newUpsells = this.upsell.upsells.filter((upsell: AddonUpsellModal) => upsell !== item);
    await this.update({
      id: this.upsell._id,
      ...this.upsell,
      upsells: this.reorder(newUpsells),
    });
    this.$stopLoading('upsell.save');
  }

  protected startEditingUpsell(item: AddonUpsellModal) {
    this.activeUpsellItem = item;
    this.editModalVisible = true;
  }

  protected getFullArticleById(id: string) {
    // @ts-ignore
    return this.articles.find((art: Article) => (this.isMaster ? (art.masterId || art._id) === id : art._id === id));
  }

  protected prettifyArticle(article: string) {
    // @ts-ignore
    const fullArticle = this.getFullArticleById(article);
    if (fullArticle) {
      return `${this.$options.filters!.localized(fullArticle.name)}`;
    }
    return article;
  }

  protected prettifyPlu(article: string) {
    // @ts-ignore
    const fullArticle = this.getFullArticleById(article);
    if (fullArticle) {
      return fullArticle.number;
    }
    return article;
  }

  @Watch('venue')
  protected async onVenueChange() {
    if (this.venue?._id && !this.$isLoading('upsell')) {
      this.$startLoading('upsell');
      await this.reload();
      this.$stopLoading('upsell');
    }
  }
}
